<script setup>
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import ChatlottaReplySuggestionsAPI from 'dashboard/api/chatlotta/replySuggestions';

const props = defineProps({
  incomingMessage: {
    type: Object,
    default: null,
  },
  editorDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['insert']);

const copy = {
  title: 'Chatlotta förslag',
  description: 'Bygger på sparad företagsinformation.',
  missingMessage:
    'Ett inkommande kundmeddelande behövs för att skapa ett förslag.',
  error: 'Förslaget kunde inte skapas. Försök igen.',
  reviewWarning: 'Granska alltid innan du skickar.',
};

const suggestion = ref(null);
const isLoading = ref(false);
const hasError = ref(false);

const incomingContent = computed(() => props.incomingMessage?.content?.trim());
const canGenerate = computed(
  () => Boolean(incomingContent.value) && !props.editorDisabled
);

const confidenceLabel = computed(() => {
  const labels = { high: 'hög', medium: 'medel', low: 'låg' };
  return labels[suggestion.value?.confidence] || suggestion.value?.confidence;
});

const sourceFields = computed(() => suggestion.value?.source_fields || []);
const isSafeForInsertion = computed(
  () =>
    suggestion.value?.needs_human_review === true &&
    suggestion.value?.can_auto_send === false
);
const suggestionMeta = computed(() => {
  if (!suggestion.value) return '';
  return `Intent: ${suggestion.value.intent} · Säkerhet: ${confidenceLabel.value}`;
});
const sourceFieldsLabel = computed(() =>
  sourceFields.value.length ? `Källfält: ${sourceFields.value.join(', ')}` : ''
);

const createSuggestion = async () => {
  if (!canGenerate.value || isLoading.value) return;

  isLoading.value = true;
  hasError.value = false;
  suggestion.value = null;

  try {
    const { data } = await ChatlottaReplySuggestionsAPI.createSuggestion(
      incomingContent.value
    );
    suggestion.value = data;
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const insertSuggestion = () => {
  if (!isSafeForInsertion.value) return;

  const reply = suggestion.value?.suggested_reply_sv;
  if (reply) emit('insert', reply);
};

watch(
  () => props.incomingMessage?.id,
  () => {
    suggestion.value = null;
    hasError.value = false;
  }
);
</script>

<template>
  <section
    class="mb-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
    data-test-id="chatlotta-reply-suggestion"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <h3 class="text-sm font-medium text-n-slate-12">{{ copy.title }}</h3>
        <p class="mt-0.5 text-xs text-n-slate-10">{{ copy.description }}</p>
      </div>
      <Button
        label="Skapa förslag"
        icon="i-lucide-sparkles"
        size="sm"
        color="slate"
        variant="outline"
        :is-loading="isLoading"
        :disabled="!canGenerate || isLoading"
        data-test-id="chatlotta-generate-suggestion"
        @click="createSuggestion"
      />
    </div>

    <p v-if="!incomingContent" class="mt-3 text-xs text-n-slate-10">
      {{ copy.missingMessage }}
    </p>

    <p v-if="hasError" class="mt-3 text-sm text-n-ruby-10" role="alert">
      {{ copy.error }}
    </p>

    <div v-if="suggestion" class="mt-3 border-t border-n-weak pt-3">
      <p
        class="whitespace-pre-wrap text-sm text-n-slate-12"
        data-test-id="chatlotta-suggested-reply"
      >
        {{ suggestion.suggested_reply_sv }}
      </p>
      <p class="mt-2 text-xs text-n-slate-10">{{ suggestionMeta }}</p>
      <p v-if="sourceFields.length" class="mt-1 text-xs text-n-slate-10">
        {{ sourceFieldsLabel }}
      </p>
      <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p class="text-xs font-medium text-n-amber-11">
          {{ copy.reviewWarning }}
        </p>
        <Button
          v-if="isSafeForInsertion"
          label="Infoga i svar"
          icon="i-lucide-corner-down-left"
          size="sm"
          data-test-id="chatlotta-insert-suggestion"
          @click="insertSuggestion"
        />
      </div>
    </div>
  </section>
</template>
